import React, {useRef, useState, useCallback} from 'react';
import {View, Animated, StyleSheet} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {PanGestureHandler, State} from 'react-native-gesture-handler';
import {SIDE_PANEL_NAVIGATION_COLOR} from 'style/theme';
import {useRemScale} from './remScale';


/**
 * Reusable on-screen joystick component.
 *
 * - Visually matches the reference-ui joystick (gold outer ring, inner lime gradient, glow).
 * - Outputs a normalized Vector2-style value in range [-1, 1] for x and y.
 * - Fully controls its own pan/animation so callers just use <Joystick onChange={...} />.
 *
 * Props:
 * - visible: toggle rendering without unmounting.
 * - invertY: if true, flips the sign of the Y value in the output Vector2 (visual stays the same).
 * - sensitivity: scales the normalized output, clamped to [-1, 1] after scaling.
 * - travelScale: 0–1 factor controlling how far the inner knob can visually travel within the ring.
 * - handlerRef: optional ref to the underlying PanGestureHandler (for coordination).
 */
const Joystick = ({
  style,
  visible = true,
  onChange,
  invertY = true,
  sensitivity = 1,
  travelScale = 2,
  handlerRef,
}) => {
  const {rem} = useRemScale();

  // Radii derived from the 1400px design, then scaled via rem
  const outerRadius = rem(92); // matches w-28 / h-28 outer circle
  const innerRadius = rem(40); // matches w-20 / h-20 inner knob

  // Limit how far the knob's center can travel so the inner circle always remains fully visible
  // inside the outer ring, even at extremes. travelScale allows reducing that visual travel.
  const baseTravelRadius = Math.max(1, outerRadius - innerRadius - rem(4));
  const clampedTravelScale =
    typeof travelScale === 'number' && isFinite(travelScale)
      ? Math.max(0.25, Math.min(travelScale, 1))
      : 1;
  const travelRadius = baseTravelRadius * clampedTravelScale;

  const joystickPan = useRef(new Animated.ValueXY({x: 0, y: 0})).current;
  const [joystickVector, setJoystickVector] = useState({x: 0, y: 0});

  const resetJoystick = useCallback(() => {
    Animated.spring(joystickPan, {
      toValue: { x: 0, y: 0 },
      useNativeDriver: true,
      friction: 6,
      tension: 80,
    }).start();
    setJoystickVector({ x: 0, y: 0 });
    if (typeof onChange === 'function') {
      onChange({ x: 0, y: 0 });
    }
  }, [joystickPan, onChange]);

  const handleGestureEvent = useCallback(
    (event) => {
      let { translationX: dx, translationY: dy } = event.nativeEvent;

      // Clamp to circular travel radius so the knob never leaves the outer ring.
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance > travelRadius && distance > 0) {
        const ratio = travelRadius / distance;
        dx *= ratio;
        dy *= ratio;
      }

      joystickPan.setValue({x: dx, y: dy});

      // Base normalized vector from joystick travel
      const normX = dx / travelRadius;
      const normY = dy / travelRadius;

      // Apply sensitivity (scaled, then clamped to [-1, 1]) and optional Y inversion.
      const sens =
        typeof sensitivity === 'number' && isFinite(sensitivity)
          ? sensitivity
          : 1;
      let outX = normX * sens;
      let outY = normY * sens;

      // Clamp after scaling to keep a joystick-like range.
      outX = Math.max(-1, Math.min(1, outX));
      outY = Math.max(-1, Math.min(1, outY));

      if (invertY) {
        outY = -outY;
      }

      let newX = outX;
      if (outY < -0.4) {
        newX = -newX;
      }
      const nextVector = {x: newX, y: outY};
      setJoystickVector(nextVector);

      if (typeof onChange === 'function') {
        onChange(nextVector);
      }

      // Debug logging for Vector2-style output – compact string for easy reading
      try {
        const xStr = nextVector.x.toFixed(2);
        const yStr = nextVector.y.toFixed(2);
        console.log(`[Joystick Vector2] x=${xStr}, y=${yStr}`);
      } catch (_) {}
    },
    [invertY, joystickPan, onChange, sensitivity, travelRadius],
  );

  const handleStateChange = useCallback(
    (event) => {
      const { state } = event.nativeEvent;
      if (
        state === State.END ||
        state === State.CANCELLED ||
        state === State.FAILED
      ) {
        resetJoystick();
      }
    },
    [resetJoystick],
  );

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        container: {
          position: 'absolute',
          zIndex: 40,
        },
        // Wrapper carries the circular drop shadow so it doesn't appear rectangular.
        outerWrapper: {
          position: 'relative',
          width: rem(112),
          height: rem(112),
          borderRadius: rem(56),
          shadowColor: '#000',
          shadowOpacity: 0.6,
          shadowRadius: rem(20),
          shadowOffset: { width: 0, height: rem(10) },
          overflow: 'visible',
        },
        outerRing: {
          width: '100%',
          height: '100%',
          borderRadius: rem(56),
          borderWidth: rem(2),
          borderColor: 'rgba(255, 255, 255, 0.16)',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          overflow: 'hidden',
        },
        outerFill: {
          width: '88%',
          height: '88%',
          borderRadius: rem(48),
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
        },
        innerKnob: {
          width: rem(80),
          height: rem(80),
          borderRadius: rem(40),
          backgroundColor: SIDE_PANEL_NAVIGATION_COLOR,
          overflow: 'visible',
          shadowColor: '#000',
          shadowOpacity: 0.35,
          shadowRadius: rem(10),
          shadowOffset: { width: 0, height: rem(6) },
          // Center the knob over the outer ring; movement is applied via translateX/translateY.
          position: 'absolute',
          left: '50%',
          top: '50%',
          marginLeft: -rem(40),
          marginTop: -rem(40),
        },
        highlight: {
          position: 'absolute',
          left: rem(8),
          top: rem(8),
          right: rem(8),
          bottom: rem(8),
          borderRadius: rem(40),
          borderWidth: rem(2),
          borderColor: 'rgba(255, 255, 255, 0.2)',
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
        },
        iconWrapper: {
          justifyContent: 'center',
          alignItems: 'center',
        },
        icon: {
          color: '#050816',
          fontSize: rem(32),
        },
        shadowEllipse: {
          position: 'absolute',
          bottom: -rem(10),
          left: '50%',
          marginLeft: -rem(32),
          width: rem(64),
          height: rem(12),
          backgroundColor: 'rgba(0,0,0,0.0)',
          borderRadius: rem(24),
          shadowColor: '#000',
          shadowOpacity: 0.7,
          shadowRadius: rem(10),
          shadowOffset: { width: 0, height: rem(4) },
          zIndex: -1,
        },
        glow: {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: rem(56),
          backgroundColor: 'rgba(50, 203, 253, 0.18)', // soft navigation-tinted glow
        },
      }),
    [rem],
  );

  if (!visible) {
    return null;
  }

  return (
    <View style={[styles.container, style]} pointerEvents="auto">
      <View style={styles.outerWrapper}>
        <View style={styles.outerRing}>
          <View style={styles.outerFill} />
        </View>

        {/* Floating inner knob – rendered above the ring, never clipped by it */}
        <PanGestureHandler
          ref={handlerRef}
          onGestureEvent={handleGestureEvent}
          onHandlerStateChange={handleStateChange}>
          <Animated.View
            style={[
              styles.innerKnob,
              {
                transform: [
                  {translateX: joystickPan.x},
                  {translateY: joystickPan.y},
                ],
              },
            ]}>
            <View style={styles.highlight} />
            <View style={styles.iconWrapper}>
              <Icon name="gesture-swipe" style={styles.icon} />
            </View>
            <View style={styles.shadowEllipse} />
          </Animated.View>
        </PanGestureHandler>

        {/* Soft outer glow behind everything */}
        <View style={styles.glow} pointerEvents="none" />
      </View>
    </View>
  );
};

export default Joystick;
